import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { gtag } from '../../Analytics/gtag';
import PowerCheckoutForm from '../../PowerCheckoutForm';
import Img from '../../Img';
import config from '../../../../config';
import links from '../../../../config/links';
import useFabletics from '../../hooks/useFabletics';
import FableticsUpgradeCreative from '../../Fabletics/UpgradeCreative';
import GlobalPrivilegesUpgradeCreative from '../../Fabletics/UpgradeCreative/GlobalPrivilegesUpgradeCreative';
import Popup from '../../Popup/PopupV2';
import { showPopupNew, closePopup } from '../../../actions/popup-actions';
import { PopupTypes, stripePlans, SubscriptionIntervalTypes } from '../../../utils/constants';
import { getPowerSubscriptionPlans, getPowerSubscriptionDetails } from '../../../api/checkoutApi';
import { applyReferralCode, clearReferralCode } from '../../../api/referralsApi';
import { getApiAxiosInstance } from '../../../utils/network';
import { isUserFableticsEligible } from '../../../utils/user';
import { POWER_UPGRADE_SUCCESS_EVENT } from '../../../utils/powerEvents';
import { intervalToBillingCycle, platform, ReferralAttemptErrorStates, ReferralEvents } from '../../Analytics/constants';
import { trackEvent } from '../../Analytics/TrackedClick';
import { usePowerFunnelTracking } from '../../hooks/usePowerFunnelTracking';
import { getReferralApplyErrorType } from '../../../utils/referrals';
import { ReferralApplyErrorMessages, ReferralApplyErrorTypes, REFERRAL_CLEAR_ERROR_MESSAGE } from '../../../../constants/referrals';
import { getLeftColumnVariantFor } from '../leftColumns/registry';
import ReferralCodeSection from './ReferralCodeSection';
import { ALL_PLANS_OFFERED, hasExplicitPlansOffered, offersInterval, resolveDefaultInterval, resolvePlansOffered } from '../../../utils/powerPlanOffering';

import styles from './upgradeV4.scss';
import popupStyles from '../../Popup/popupv2.scss';

const SubscriptionIntervals = SubscriptionIntervalTypes;

const API_STATUS = {
    SUCCESS: 'success',
};

const ON_SUCCESS_TIMEOUT_MS = 5000;

// code_expired, code_redeemed, and incorrect_plan_interval are absent: they collapse into
// isValidCode:false on the current apply response (see ENG-2315), so they can't be told apart yet.
const APPLY_ERROR_TO_ATTEMPT_STATE = {
    [ReferralApplyErrorTypes.SELF_REFERRAL]: ReferralAttemptErrorStates.SELF_CODE,
    [ReferralApplyErrorTypes.INVALID_CODE]: ReferralAttemptErrorStates.CODE_INVALID,
    [ReferralApplyErrorTypes.DISABLED_REFERER]: ReferralAttemptErrorStates.REFERRER_MEMBER_EXPIRED,
};

const UpgradeToPowerPopupV4 = (props) => {
    const { showHotelPlannerCard = true, popupType, referringSurface, gatedFeature, prismicTitle, initialReferralCode } = props || {};
    const resolvedPlansOffered = resolvePlansOffered({ plansOffered: props?.plansOffered, showHotelPlannerCard });
    const isAnnualOffered = offersInterval(resolvedPlansOffered, SubscriptionIntervals.YEAR);
    const isMonthlyOffered = offersInterval(resolvedPlansOffered, SubscriptionIntervals.MONTH);
    const showBothPlanOptions = isAnnualOffered && isMonthlyOffered;
    // Callers that pass plansOffered get only their offered plans rendered. Callers that
    // don't keep the legacy presentation, where an unavailable plan still renders greyed
    // out, so the existing Hotel Planner popup is untouched.
    const hidesUnofferedPlans = hasExplicitPlansOffered(props?.plansOffered);
    const [interval, setInterval] = useState(resolveDefaultInterval(resolvedPlansOffered));
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);
    const [couponCode, setCouponCode] = useState(null);
    const [couponError, setCouponError] = useState('');
    const [invoiceItems, setInvoiceItems] = useState(null);
    const [isLoadingPricing, setIsLoadingPricing] = useState(true);
    const [binCountryError, setBinCountryError] = useState('');
    const [isSuccessLoading, setIsSuccessLoading] = useState(false);
    const isSuccessLoadingRef = useRef(false);
    const hasResolvedReferralRef = useRef(false);
    // Serializes concurrent invoice fetches: each fetch claims the next id and writes only if
    // still current, so a slower undiscounted load can't clobber the referral discount that is
    // auto-applied on mount. Claimed in loadSubscriptionDetails and applyReferral.
    const invoiceRequestIdRef = useRef(0);
    const [isTennisAustraliaMember, setIsTennisAustraliaMember] = useState(false);
    const [referralInput, setReferralInput] = useState(initialReferralCode || '');
    const [appliedReferralCode, setAppliedReferralCode] = useState(null);
    const [referralError, setReferralError] = useState('');
    const [isApplyingReferral, setIsApplyingReferral] = useState(false);
    const popups = useSelector(({ popups }) => popups);
    const user = useSelector(({ auth }) => auth?.user);
    const dispatch = useDispatch();
    const { enabled: isFableticsEnabled, isUpsellAudience } = useFabletics();
    const isUSEligible = isUserFableticsEligible(user);
    // Non-US free viewers get the Global Privileges upgrade creative (travel image
    // + GP caption) in place of the Hotel Planner card, and their plan copy never
    // mentions Fabletics (US-only). US and logged-out viewers are unaffected.
    const isNonUsFreeUser = !!user && !isUSEligible && !user.isPowered;
    const LeftColumnVariant = getLeftColumnVariantFor(gatedFeature);
    const isCppContext = !!LeftColumnVariant;

    const isAnnual = interval === SubscriptionIntervals.YEAR;
    const showReferralField = isAnnual;
    const referralDirty = showReferralField && referralInput.trim() !== '' && referralInput.trim() !== (appliedReferralCode || '');
    const isCheckoutDisabledByReferral = isApplyingReferral || referralDirty;
    // A referral code is in play once one is typed, being applied, or already applied. While it is,
    // the annual-vs-monthly "(Save X%)" is suppressed: it doesn't reflect the referral discount, and
    // it otherwise flashes before the discounted invoice replaces it. The discounted line items carry
    // the real savings.
    const referralInPlay = showReferralField && (referralInput.trim() !== '' || Boolean(appliedReferralCode));

    // referringSurface reports where the popup was actually opened from. Applying a code here
    // does not make this the referral landing page — only the landing page itself passes that
    // surface, so a code typed at checkout keeps the surface the user really came through.
    const { trackPowerPaywallViewed, trackPowerPurchaseSucceeded } = usePowerFunnelTracking({ referringSurface, gatedFeature, prismicTitle });

    const effectivePopupType = popupType || (showHotelPlannerCard ? PopupTypes.POWER_UPGRADE_POPUP : PopupTypes.UPGRADE_TO_POWER_POPUP);
    const onPaymentAttempted = popups[`${effectivePopupType}OnPaymentAttempted`] || null;

    const fetchSubscriptionPlans = async () => {
        try {
            const response = await getPowerSubscriptionPlans(getApiAxiosInstance());
            if (response?.data?.subscriptionPlans) {
                setSubscriptionPlans(response.data.subscriptionPlans);
            }
            if (response?.data?.isTennisAustraliaMember) {
                setIsTennisAustraliaMember(true);
            }
        } catch {
            setSubscriptionPlans([]);
        }
    };

    const isV2022Plan = (targetInterval = null, plansToUse = null) => {
        const intervalToUse = targetInterval || interval;
        const plansArray = plansToUse || subscriptionPlans;
        const selectedPlan = plansArray.find(plan => plan.interval === intervalToUse);
        const planId = selectedPlan?.planId;
        return planId === stripePlans.members.powerAnnual || planId === stripePlans.members.powerMonthly;
    };

    const fetchSubscriptionDetails = async (targetInterval = null, couponCodeParam = null, plansToUse = null) => {
        try {
            const intervalToUse = targetInterval || interval;
            const plansArray = plansToUse || subscriptionPlans;
            const selectedPlan = plansArray.find(plan => plan.interval === intervalToUse);
            const planId = selectedPlan?.planId;

            if (!planId) {
                if (couponCodeParam) {
                    setCouponError('Plan not available');
                }
                return null;
            }

            const apiParams = { plan: planId };
            if (couponCodeParam) {
                apiParams.couponCode = couponCodeParam;
            }

            const response = await getPowerSubscriptionDetails(getApiAxiosInstance(), apiParams);
            if (response.data.status === API_STATUS.SUCCESS) {
                if (couponCodeParam) {
                    setCouponError('');
                }
                return response.data.data;
            }

            if (couponCodeParam) {
                setCouponError(response.data.message || 'Invalid coupon code');
            }
            return null;
        } catch {
            if (couponCodeParam) {
                setCouponError('Error applying coupon');
            }
            return null;
        }
    };

    useEffect(() => {
        fetchSubscriptionPlans();
        trackPowerPaywallViewed();
    }, []);

    const onCloseClickRef = useRef(props.onCloseClick);
    useEffect(() => { onCloseClickRef.current = props.onCloseClick; });
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && !isSuccessLoadingRef.current) {
                onCloseClickRef.current();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        const loadSubscriptionDetails = async () => {
            if (subscriptionPlans.length > 0) {
                invoiceRequestIdRef.current += 1;
                const requestId = invoiceRequestIdRef.current;
                setIsLoadingPricing(true);
                const requiresSubscriptionDetailsForV2022Plan = isV2022Plan();
                if (requiresSubscriptionDetailsForV2022Plan) {
                    const invoiceData = await fetchSubscriptionDetails();
                    if (requestId === invoiceRequestIdRef.current) {
                        setInvoiceItems(invoiceData);
                    }
                } else {
                    setInvoiceItems(null);
                }
                setIsLoadingPricing(false);
            }
        };
        loadSubscriptionDetails();
    }, [subscriptionPlans, interval]);

    const handleSuccess = async () => {
        window.dispatchEvent(new CustomEvent(POWER_UPGRADE_SUCCESS_EVENT));

        const planData = getSelectedPlan();
        const billingCycle = intervalToBillingCycle[planData?.interval] || null;

        trackPowerPurchaseSucceeded({
            billingCycle,
            priceLocal: planData?.amountPayPerRecurringInterval,
            currency: planData?.currency || 'USD',
        });

        gtag('power-upgrade', {
            category: 'Power Upgrade',
            // Keyed off the popup slot, not the Hotel Planner card: the card is now an
            // independent prop (ENG-2546), so hiding it must not relabel the event.
            context: effectivePopupType === PopupTypes.POWER_UPGRADE_POPUP
                ? 'Power Upgrade success'
                : 'Hotel Planner Upgrade success',
        });

        const onSuccess = popups[`${effectivePopupType}OnSuccess`];

        if (onSuccess) {
            // Keep the popup open and show a loading overlay while onSuccess runs
            // (e.g. the Rally chat path awaits fetchAuthenticatedUser here before
            // reopening the chat). Close the popup only once the work is done so
            // the user always sees a continuous UI transition rather than a blank gap.
            isSuccessLoadingRef.current = true;
            setIsSuccessLoading(true);
            try {
                const timeoutPromise = new Promise((_, reject) => {
                    setTimeout(() => reject(new Error('onSuccess timed out')), ON_SUCCESS_TIMEOUT_MS);
                });
                await Promise.race([onSuccess(), timeoutPromise]);
            } catch (e) {
                console.error('onSuccess failed, closing popup anyway', e);
            } finally {
                isSuccessLoadingRef.current = false;
                setIsSuccessLoading(false);
            }
            dispatch(closePopup(effectivePopupType));
        } else {
            dispatch(closePopup(effectivePopupType));
            dispatch(showPopupNew({
                popup: PopupTypes.POWER_SUCCESS_POPUP_V2,
                redirectTo: popups.redirectTo,
            }));
        }
    };

    const handleBinCountryMismatch = async (availablePowerPlans, errorMessage) => {
        setIsLoadingPricing(true);
        if (availablePowerPlans.subscriptionPlans && availablePowerPlans.subscriptionPlans.length > 0) {
            setSubscriptionPlans(availablePowerPlans.subscriptionPlans);
        }
        if (availablePowerPlans.isTennisAustraliaMember !== undefined) {
            setIsTennisAustraliaMember(availablePowerPlans.isTennisAustraliaMember);
        }
        setBinCountryError(errorMessage);
        setCouponCode(null);
        setCouponError('');

        const newPlans = availablePowerPlans.subscriptionPlans || subscriptionPlans;
        const requiresSubscriptionDetailsForV2022Plan = isV2022Plan(interval, newPlans);

        if (requiresSubscriptionDetailsForV2022Plan) {
            const invoiceData = await fetchSubscriptionDetails(interval, null, newPlans);
            setInvoiceItems(invoiceData);
        } else {
            setInvoiceItems(null);
        }

        // Paying can swap the plan set for a different billing country, and the server validated
        // any applied referral against the plan it replaced — including whether the campaign can
        // discount that currency at all. Re-apply against the replacement so the discount either
        // carries over or reports why it cannot, rather than leaving a stale line item.
        // Gated on showReferralField: the input survives a switch to monthly, and re-applying
        // there would send the monthly planId and surface an error the hidden field can't show.
        if (showReferralField && referralInput.trim() !== '') {
            await applyReferral({ plansToUse: newPlans, isUserInitiated: false });
        }

        setIsLoadingPricing(false);
    };

    const applyCoupon = async (couponCodeParam) => {
        if (!couponCodeParam || couponCodeParam.trim() === '') {
            setCouponError('Please enter a coupon code');
            return;
        }

        const isV2022 = isV2022Plan();
        if (isV2022) {
            const invoiceData = await fetchSubscriptionDetails(null, couponCodeParam.trim());
            setInvoiceItems(invoiceData);
            if (invoiceData) {
                setCouponCode(couponCodeParam.trim());
            }
        } else {
            setCouponError('Coupon codes are not supported for this plan');
        }
    };

    const handleReferralInputChange = (value) => {
        setReferralInput(value);
        if (referralError) {
            setReferralError('');
        }
    };

    const handleReferralApplyError = (errorType) => {
        const resolvedType = errorType || ReferralApplyErrorTypes.INVALID_CODE;
        setReferralError(ReferralApplyErrorMessages[resolvedType]);
        // A region-blocked code is valid but unusable at this billing country, so there is nothing
        // for the user to correct. Emptying the field is what the message promises, and it also
        // clears referralDirty so checkout is not held hostage to a code that can never apply.
        // Every other error leaves the input alone — those the user can fix or clear themselves.
        // The Applied marker is dropped by the caller, which owns that decision for every error.
        if (resolvedType === ReferralApplyErrorTypes.OFFER_UNAVAILABLE_LOCATION) {
            setReferralInput('');
        }
    };

    // plansToUse lets a caller apply against a plan set that has not reached state yet, which the
    // BIN-country retry needs. isUserInitiated is false for the automatic applies (the BIN-country
    // retry and the initial auto-apply of a code from the referral link) — on failure those drop
    // the Applied marker, since the user did not type the code and has nothing to correct.
    const applyReferral = async ({ plansToUse = null, isUserInitiated = true, clearFieldOnReject = false } = {}) => {
        const code = referralInput.trim();
        setReferralError('');
        if (!code) {
            return;
        }
        setIsApplyingReferral(true);
        try {
            // planId is required by the apply contract: the server resolves the Stripe plan to
            // confirm the campaign can discount it in that plan's currency. The referral field
            // only renders on the annual plan, so the selected plan is the one being discounted.
            const response = await applyReferralCode(getApiAxiosInstance(), {
                codeSlug: code,
                planId: getSelectedPlan(plansToUse)?.planId,
            });
            const errorType = getReferralApplyErrorType(response);
            if (errorType) {
                handleReferralApplyError(errorType);
                // Fire referral_attempt_failed for a user-initiated apply the server rejected, for
                // the failure reasons the apply response can distinguish. referrer_type /
                // referrer_id / referee_id are omitted until the apply response returns referrer
                // identity at checkout (see ENG-2315).
                const errorState = APPLY_ERROR_TO_ATTEMPT_STATE[errorType];
                if (isUserInitiated && errorState) {
                    /* eslint-disable camelcase */
                    trackEvent(ReferralEvents.REFERRAL_ATTEMPT_FAILED, {
                        referral_code: code,
                        error_state: errorState,
                        platform,
                    });
                    /* eslint-enable camelcase */
                }
                // A code seeded from the referral link, not typed by the user, must not stay
                // prefilled once the server rejects it (a self-referral, for one): the user never
                // entered it and has nothing to correct. Scoped to the initial auto-apply — the
                // billing-country retry leaves a user-recoverable code in place (only a region
                // block clears it, handled by handleReferralApplyError). Thrown transport failures
                // land in the catch below, so a network blip never discards a valid seeded code.
                if (clearFieldOnReject) {
                    setReferralInput('');
                }
                // Drop the Applied marker once the verdict makes it untrue: any non-user-initiated
                // rejection, or a region block whoever triggered it.
                if (!isUserInitiated || errorType === ReferralApplyErrorTypes.OFFER_UNAVAILABLE_LOCATION) {
                    setAppliedReferralCode(null);
                }
                return;
            }
            // Apply persists the referral server-side, so re-fetching the invoice (no coupon)
            // returns the balance-credit line item that reflects the discount.
            invoiceRequestIdRef.current += 1;
            const requestId = invoiceRequestIdRef.current;
            const invoiceData = await fetchSubscriptionDetails(null, null, plansToUse);
            if (invoiceData && requestId === invoiceRequestIdRef.current) {
                setInvoiceItems(invoiceData);
            }
            hasResolvedReferralRef.current = true;
            setAppliedReferralCode(code);
            // referral_code_apply fires only when the user actually clicked Apply — not the
            // auto-apply from the landing link or the billing-country retry. referrer_type /
            // referrer_id are omitted until the apply response returns referrer identity (ENG-2315).
            if (isUserInitiated) {
                /* eslint-disable camelcase */
                trackEvent(ReferralEvents.REFERRAL_CODE_APPLY, {
                    referral_code: code,
                    is_logged_in: Boolean(user),
                    platform,
                });
                /* eslint-enable camelcase */
            }
        } catch (error) {
            handleReferralApplyError(getReferralApplyErrorType(error));
        } finally {
            setIsApplyingReferral(false);
        }
    };

    // When the popup is opened from the referral landing "Get Power" CTA, the code arrives as
    // initialReferralCode. Auto-apply it once the plans are loaded so the discount is set on
    // the invoice before checkout — the subscribe call then attributes the referral via cookie.
    useEffect(() => {
        if (hasResolvedReferralRef.current) {
            return;
        }
        if (subscriptionPlans.length === 0 || !initialReferralCode || !showReferralField) {
            return;
        }
        hasResolvedReferralRef.current = true;
        // Not user-initiated: the code came from the referral link, not the user's typing. So it
        // must not spend a manual attempt, and if the server rejects it (e.g. self-referral) it is
        // cleared from the field rather than left prefilled with an error.
        applyReferral({ isUserInitiated: false, clearFieldOnReject: true });
    }, [subscriptionPlans, initialReferralCode, showReferralField]);

    // When checkout is reached WITHOUT an explicit landing code but the member already has a
    // server-captured referral (attributed via the referral_token cookie), GET subscribe echoes
    // it as data.appliedReferralCode and the returned invoice is already discounted. Reflect it
    // in the field as applied — no re-apply POST, since the cookie drives the final checkout.
    // Runs at most once: the ref keeps clearReferral's invoice re-fetch from repopulating the field.
    useEffect(() => {
        if (hasResolvedReferralRef.current || initialReferralCode || !showReferralField) {
            return;
        }
        const capturedCode = invoiceItems?.appliedReferralCode;
        if (capturedCode) {
            hasResolvedReferralRef.current = true;
            setReferralInput(capturedCode);
            setAppliedReferralCode(capturedCode);
        }
    }, [invoiceItems, initialReferralCode, showReferralField]);

    const clearReferral = async () => {
        setReferralInput('');
        setReferralError('');
        if (!appliedReferralCode) {
            return;
        }
        const clearedCode = appliedReferralCode;
        setAppliedReferralCode(null);
        try {
            await clearReferralCode(getApiAxiosInstance());
        } catch {
            // The server still holds the referral cookie, so the invoice keeps the discount.
            // Restore the applied state so the field matches the server and the user can retry.
            setReferralInput(clearedCode);
            setAppliedReferralCode(clearedCode);
            setReferralError(REFERRAL_CLEAR_ERROR_MESSAGE);
            return;
        }
        if (isV2022Plan()) {
            const invoiceData = await fetchSubscriptionDetails();
            if (invoiceData) {
                setInvoiceItems(invoiceData);
            }
        }
    };

    const getYearlyPlan = () => {
        return subscriptionPlans.find(plan => plan.interval === SubscriptionIntervals.YEAR);
    };

    const getMonthlyPlan = () => {
        return subscriptionPlans.find(plan => plan.interval === SubscriptionIntervals.MONTH);
    };

    const getSelectedPlan = (plansToUse = null) => {
        return (plansToUse || subscriptionPlans).find(plan => plan.interval === interval);
    };

    const getSavingsPercentage = () => {
        const yearlyPlan = getYearlyPlan();
        return yearlyPlan ? yearlyPlan.savingsPercentage : 0;
    };

    const formatPrice = (price) => {
        if (!price) { return price; }
        return price.replace(/\.00$/, '');
    };

    const handlePlanSelect = (selectedInterval) => {
        setInterval(selectedInterval);
    };

    const getHeaderClass = () => {
        if ((isFableticsEnabled && !isUpsellAudience) || isNonUsFreeUser) {
            return styles.memberHeader;
        }
        return styles.header;
    };

    const getHeaderTitle = () => {
        if (isCppContext) {
            return 'Start Annual Power';
        }
        if (isFableticsEnabled || isNonUsFreeUser) {
            return 'Power pays for itself!';
        }
        return 'Power: The tennis membership that pays for itself.';
    };

    const renderDefaultLeftColumn = () => (
        <>
            <div className={styles.intro}>
                <div className={styles.introTitle} data-testid="modal.power-upgrade.intro-title">
                    You are one step away from elevating your game.
                </div>
            </div>
            <div className={styles.benefitsBox}>
                <div className={styles.benefitsBoxTitle} data-testid="modal.power-upgrade.benefits-title">
                    A Power membership unlocks:
                </div>
                <ul className={styles.benefitsList}>
                    <li data-testid="modal.power-upgrade.benefit-0">See precise 2 decimal ratings for all players</li>
                    <li data-testid="modal.power-upgrade.benefit-1">View in-depth performance analytics</li>
                    <li data-testid="modal.power-upgrade.benefit-2">Get discounted entry fees</li>
                    <li data-testid="modal.power-upgrade.benefit-3">Access advanced tools for college recruits</li>
                    <li data-testid="modal.power-upgrade.benefit-4">Discounts on travel, apparel, gear, and so much more</li>
                </ul>
            </div>
            {isNonUsFreeUser ? (
                <GlobalPrivilegesUpgradeCreative />
            ) : (
                showHotelPlannerCard && (
                    <div className={styles.hotelPlannerSection}>
                        <div className={styles.hotelPlannerImageWrapper}>
                            <img
                                src="/img/v3/graphics/perks/hotel-planner.jpg"
                                alt="Tennis court resort"
                                className={styles.hotelPlannerImage}
                                data-testid="modal.power-upgrade.hotel-img"
                            />
                        </div>
                        <div className={styles.hotelPlannerContent}>
                            <p className={styles.hotelPlannerHeadline} data-testid="modal.power-upgrade.hotel-headline">
                                Sign up for a Power annual plan and unlock:
                            </p>
                            <ul className={styles.hotelPlannerBenefits}>
                                <li data-testid="modal.power-upgrade.hotel-benefit-0">Over $250 of value in travel benefits</li>
                                <li data-testid="modal.power-upgrade.hotel-benefit-1">Up to 60% off on 1.4 million hotels globally</li>
                            </ul>
                            <button
                                type="button"
                                className={styles.hotelPlannerLearnMore}
                                onClick={() => window.open(links.powerPlayersPage, '_blank', 'noopener,noreferrer')}
                                data-testid="modal.power-upgrade.hotel-learn-more-btn"
                            >
                                <span className={styles.underline}>Learn more</span> about Global Privileges
                            </button>
                        </div>
                    </div>
                )
            )}
        </>
    );

    const renderLeftColumn = () => {
        if (LeftColumnVariant) {
            return <LeftColumnVariant />;
        }
        if (isFableticsEnabled) {
            return (
                <div className={styles.fableticsCreativeWrapper}>
                    <FableticsUpgradeCreative />
                </div>
            );
        }
        return renderDefaultLeftColumn();
    };

    const modalHeader = () => {
        const headerClass = getHeaderClass();
        return (
            <div className={`${popupStyles.modalHeader} ${headerClass}`}>
                <div className={styles.headerSpacer} />
                <p className={styles.headerTitle} data-testid="modal.power-upgrade.title">{getHeaderTitle()}</p>
                <button
                    type="button"
                    className={`${popupStyles.btnClose} ${styles.closeBtn}`}
                    onClick={props.onCloseClick}
                    aria-label="Close"
                    style={isSuccessLoading ? { visibility: 'hidden', pointerEvents: 'none' } : undefined}
                    aria-hidden={isSuccessLoading || undefined}
                    data-testid="modal.power-upgrade.close-btn"
                >
                    <i className="utr utr-close" aria-hidden />
                </button>
            </div>
        );
    };

    const renderPaymentCards = () => (
        <div className={styles.cards}>
            <span className={styles.cardsText} data-testid="modal.power-upgrade.cards-text">We accept all major credit cards.</span>
            <Img src="/img/v3/graphics/cards/amex-new.svg" data-testid="modal.power-upgrade.amex-img" />
            <Img src="/img/v3/graphics/cards/mastercard-new.svg" data-testid="modal.power-upgrade.mastercard-img" />
            <Img src="/img/v3/graphics/cards/visa-new.svg" className={styles.visaCard} data-testid="modal.power-upgrade.visa-img" />
            <Img src="/img/v3/graphics/cards/discover-new.svg" data-testid="modal.power-upgrade.discover-img" />
        </div>
    );

    const renderPlanSelection = () => (
        <div className={styles.planSection}>
            {showBothPlanOptions && <div className={styles.planTitle} data-testid="modal.power-upgrade.plan-title">Choose Your Plan:</div>}
            <div className={styles.planOptions}>
                {(!hidesUnofferedPlans || isAnnualOffered) && (
                <div
                    className={`${styles.planOption} ${interval === SubscriptionIntervals.YEAR ? styles.selected : ''} ${!isAnnualOffered ? styles.disabled : ''}`}
                    onClick={() => isAnnualOffered && handlePlanSelect(SubscriptionIntervals.YEAR)}
                    data-testid="modal.power-upgrade.annual-plan-option"
                >
                    <div className={styles.planDetails}>
                        <div className={styles.planName} data-testid="modal.power-upgrade.annual-plan-name">Annual Plan</div>
                        <div className={styles.planPrice} data-testid="modal.power-upgrade.annual-plan-price">
                            {formatPrice(getYearlyPlan()?.displayAmountPayPerRecurringInterval) || '$120'}
                        </div>
                        <div className={styles.planMonthly} data-testid="modal.power-upgrade.annual-plan-monthly-label">
                            {formatPrice(getYearlyPlan()?.displayMonthlyAmount) || '$10'}/Month - <span className={styles.bestValue} data-testid="modal.power-upgrade.best-value-label">BEST VALUE</span>
                        </div>
                    </div>
                    <div className={styles.planCheckmark}>
                        {interval === SubscriptionIntervals.YEAR ? (
                            <Img src="/img/v4/icon-checkmark.svg" />
                        ) : (
                            <div className={styles.emptyCheckmark} />
                        )}
                    </div>
                </div>
                )}
                {(!hidesUnofferedPlans || isMonthlyOffered) && (
                <div
                    className={`${styles.planOption} ${interval === SubscriptionIntervals.MONTH ? styles.selected : ''} ${!isMonthlyOffered ? styles.disabled : ''}`}
                    onClick={() => isMonthlyOffered && handlePlanSelect(SubscriptionIntervals.MONTH)}
                    data-testid="modal.power-upgrade.monthly-plan-option"
                >
                    <div className={styles.planDetails}>
                        <div className={styles.planName} data-testid="modal.power-upgrade.monthly-plan-name">Monthly Plan</div>
                        <div className={styles.planPrice} data-testid="modal.power-upgrade.monthly-plan-price">
                            {formatPrice(getMonthlyPlan()?.displayAmountPayPerRecurringInterval) || '$12'}/Month
                        </div>
                        {(isFableticsEnabled || isNonUsFreeUser) && (
                            <div className={styles.planExcludes} data-testid="modal.power-upgrade.monthly-plan-excludes">
                                {isUSEligible
                                    ? 'Excludes Fabletics and Global Privileges'
                                    : 'Excludes Global Privileges'}
                            </div>
                        )}
                    </div>
                    <div className={styles.planCheckmark}>
                        {interval === SubscriptionIntervals.MONTH ? (
                            <Img src="/img/v4/icon-checkmark.svg" />
                        ) : (
                            <div className={styles.emptyCheckmark} />
                        )}
                    </div>
                </div>
                )}
            </div>
            {binCountryError && (
                <div className={styles.binCountryError}>
                    <div className={styles.errorMessage}>
                        <div className={styles.errorTitle} data-testid="modal.power-upgrade.bin-error-title">
                            Billing Country Does Not Match Your Residence
                        </div>
                        <div className={styles.errorText} data-testid="modal.power-upgrade.bin-error-text">
                            Our subscription pricing is based on your billing country. The rates have been updated accordingly.
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className={`${styles.popup} overlay lite-black`}>
            <Popup
                className="popup-lg"
                config={{
                    size: 'lg',
                    scrollable: true,
                    customHeader: modalHeader,
                    customWidth: '900px',
                    bodyClassName: styles.modalBodyMobile,
                }}
                onCloseClick={props.onCloseClick}
            >
                <div style={{ position: 'relative' }}>
                    <div className={styles.content}>
                        <div className={`${styles.leftColumn} ${(!showHotelPlannerCard || isNonUsFreeUser) ? styles.noCard : ''}`}>
                            {renderLeftColumn()}
                        </div>
                        <div className={styles.rightColumn}>
                            <div className={styles.formSection}>
                                <div className={styles.planSelectionWrapper}>
                                    {renderPlanSelection()}
                                </div>
                                <div className={styles.formTitle} data-testid="modal.power-upgrade.form-title">
                                    Please enter your credit card information below.
                                </div>
                                <PowerCheckoutForm
                                    interval={interval}
                                    onComplete={handleSuccess}
                                    onPaymentAttempted={onPaymentAttempted}
                                    hideHeader
                                    themeV3
                                    planData={referralInPlay ? { ...getSelectedPlan(), savingsPercentage: 0 } : getSelectedPlan()}
                                    couponCode={couponCode}
                                    couponError={couponError}
                                    applyCoupon={applyCoupon}
                                    invoiceItems={invoiceItems}
                                    onBinCountryMismatch={handleBinCountryMismatch}
                                    isLoadingPricing={isLoadingPricing}
                                    submitButtonText="START POWER"
                                    isTennisAustraliaMember={isTennisAustraliaMember}
                                    savingsPercentage={referralInPlay ? 0 : getSavingsPercentage()}
                                    referringSurface={props.referringSurface}
                                    gatedFeature={props.gatedFeature}
                                    disabled={isCheckoutDisabledByReferral}
                                    // Slot rendered between the card fields and the pricing/submit
                                    // row — the spot where the design wants the "We accept all
                                    // major credit cards" line, followed by the referral-code field.
                                    renderBeforeLineItem={() => (
                                        <>
                                            {renderPaymentCards()}
                                            {showReferralField && (
                                                <ReferralCodeSection
                                                    value={referralInput}
                                                    onChange={handleReferralInputChange}
                                                    appliedCode={appliedReferralCode}
                                                    error={referralError}
                                                    isApplying={isApplyingReferral}
                                                    onApply={applyReferral}
                                                    onClear={clearReferral}
                                                    data-testid="modal.power-upgrade.referral"
                                                />
                                            )}
                                        </>
                                    )}
                                    prismicTitle={prismicTitle}
                                />
                                <div className={styles.disclaimer} data-testid="modal.power-upgrade.disclaimer">
                                    By tapping the button, I agree to the <a href={`${config?.marketingAppHost}/policies/terms-of-service`} target="_blank" rel="noopener noreferrer" data-testid="modal.power-upgrade.terms-link">Terms of Service</a> and automatic charges until I cancel.
                                </div>
                            </div>
                        </div>
                    </div>
                    {isSuccessLoading && (
                        <div className={styles.successLoadingOverlay} data-testid="modal.power-upgrade.success-loading-overlay">
                            <div className={styles.successLoadingContent}>
                                <div className="spinner-border text-info" role="status">
                                    <span className="sr-only" data-testid="modal.power-upgrade.loading-sr-text">Loading...</span>
                                </div>
                                <p data-testid="modal.power-upgrade.loading-message">Updating your account...</p>
                            </div>
                        </div>
                    )}
                </div>
            </Popup>
        </div>
    );
};

UpgradeToPowerPopupV4.propTypes = {
    onCloseClick: PropTypes.func.isRequired,
    showHotelPlannerCard: PropTypes.bool,
    plansOffered: PropTypes.arrayOf(PropTypes.oneOf(ALL_PLANS_OFFERED)),
    popupType: PropTypes.string,
    referringSurface: PropTypes.string.isRequired,
    gatedFeature: PropTypes.string,
    prismicTitle: PropTypes.string,
    initialReferralCode: PropTypes.string,
};

UpgradeToPowerPopupV4.defaultProps = {
    showHotelPlannerCard: true,
    plansOffered: null,
    popupType: null,
    gatedFeature: null,
    prismicTitle: null,
    initialReferralCode: null,
};

export default UpgradeToPowerPopupV4;
